import { Component, HostListener, Inject, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { UserLanguage, UserModel } from 'app/account-settings/models/user.model';
import { Account } from 'app/back-office/account.model';
import { OnsiteCampaign, OnsiteCampaignType, ReferralTypes, TargetDevice } from 'app/campaign/campaign.model';
import { WidgetEditorDataService } from 'app/campaign/widget-editor-data.service';
import { CampaignTemplatesService } from 'app/campaign/templates/campaign-templates.service';
import { CampaignTemplate, TemplateTypeForNewCategories } from 'app/campaign/templates/template.model';
import { Widget, WidgetDeviceView } from 'app/campaign/widgets.model';
import { CurrentContextService } from 'app/core/current-context.service';
import { Logger, LoggerFactory } from 'app/core/logger.service';
import { ENVIRONMENT_DI } from 'app/environment/environment';
import { Environment } from 'app/environment/environment.base';
import { FlyoutDataType, FlyoutOpeningData } from 'app/flyout/flyout-models';
import { injectReferralsAllowed } from 'app/shared/account/referrals-allowed';
import { CampaignService } from 'app/shared/services/campaign.service';
import { WidgetService } from 'app/shared/services/widget.service';
import { Subscription } from 'rxjs';
import { FlyoutService } from '../flyout.service';
import { PageTitle } from 'app/shared/utils/page-title';

@Component({
  selector: 'app-flyout-widget-preview',
  templateUrl: './flyout-widget-preview.component.html',
  styleUrls: [
    './flyout-widget-preview.component.scss',
    '../../shared/device-buttons/smartphone.scss',
    '../../shared/device-buttons/tablet.scss',
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutWidgetPreviewIframeComponent implements OnInit, OnDestroy {
  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (event.code === 'ArrowRight') {
      this.nextTemplate();
    }
    if (event.code === 'ArrowLeft') {
      this.previousTemplate();
    }
  }
  // Common properties
  @Input() flyoutOpeningData: FlyoutOpeningData;
  private readonly logger: Logger;
  private subscriptionArray: Subscription[] = [];
  private currentAccount: Account;
  private currentUser: UserModel;
  private currentWidgetId: number;
  private templates: CampaignTemplate[];
  private template: CampaignTemplate;
  private readonly referralsAllowed = injectReferralsAllowed();
  public DeviceViewEnums = WidgetDeviceView;
  public iframeUrl: SafeUrl;
  public iframeWidth: number;
  public iframeHeight: number;
  public iframeScale = 1;
  public currentDeviceView: WidgetDeviceView = WidgetDeviceView.Desktop;
  public WidgetDeviceView = WidgetDeviceView;
  // Native-app campaigns preview on a tablet: the non-mobile view uses a tablet frame instead of the browser chrome.
  public isNativeApp = false;
  public isTemplatePreview: boolean;
  private campaignId: number; // Template use same property
  public PageTitle = PageTitle;
  public pageQuickPreview: string = PageTitle.Templates;
  public OnsiteCampaignType = OnsiteCampaignType;
  public isLoading = false;

  // Template preview only
  private currentTemplateIndex?: number;
  public templateName?: string;
  public authorName?: string;
  public authorImage?: string;
  public authorUrl?: string;
  public templateDescriptionItems: string[];
  public purposeId?: number;
  public showTemplatesNavigation: boolean;
  public isFirstTemplate: boolean;
  public isLastTemplate: boolean;

  constructor(
    readonly loggerFactory: LoggerFactory,
    @Inject(ENVIRONMENT_DI) private readonly environment: Environment,
    private readonly sanitizer: DomSanitizer,
    private readonly currentContextService: CurrentContextService,
    private readonly widgetService: WidgetService,
    private readonly campaignService: CampaignService,
    private readonly router: Router,
    public flyoutService: FlyoutService,
    private readonly widgetEditorDataService: WidgetEditorDataService,
  ) {
    this.logger = loggerFactory.get('FlyoutWidgetPreviewIframeComponent');
  }

  ngOnInit(): void {
    this.isNativeApp = this.widgetEditorDataService.campaignBS.getValue()?.targetDevice === TargetDevice.NativeApp;
    this.subscriptionArray.push(
      this.currentContextService.currentContextBS.subscribe((currentContext) => {
        if (currentContext) {
          const widgetPreviewContext = this.flyoutOpeningData.widgetPreviewContext;
          this.currentAccount = currentContext.account;
          this.currentUser = currentContext.user;
          this.currentWidgetId = widgetPreviewContext.widgetId;
          this.campaignId = widgetPreviewContext.campaignId;
          this.templates = widgetPreviewContext.filteredTemplates;
          // Only the campaign designer passes a device view, so anywhere else it has to come from the
          // account setting: without one, neither the browser chrome nor the phone frame renders.
          this.currentDeviceView = widgetPreviewContext.deviceView ?? this.widgetEditorDataService.defaultDeviceView;
          // Device buttons read this subject, so seeding it keeps their selection on the frame shown here.
          // Guarded because the editor behind the flyout re-renders on every emission.
          if (this.widgetEditorDataService.currentDeviceView !== this.currentDeviceView) {
            this.widgetEditorDataService.currentDeviceViewBS.next(this.currentDeviceView);
          }
          this.pageQuickPreview = !this.templates?.length
            ? PageTitle.Editor
            : this.templates?.length === 1
              ? PageTitle.TemplateSettings
              : PageTitle.Templates;
          if (!widgetPreviewContext.siteId) {
            this.setTemplateSettings(widgetPreviewContext.campaignId);
            this.purposeId = widgetPreviewContext.purposeId;
            this.isTemplatePreview = true; // Set when rest is done so template-details is not shown before fully loaded
          }
          this.setIframeSettings(); // After isTemplatePreview eventually is set
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }

  private setTemplateSettings(templateId: number): void {
    if (this.pageQuickPreview === PageTitle.Editor) return;
    this.currentTemplateIndex = this.templates.findIndex((x) => x.id === templateId);
    this.template = this.templates.find((x) => x.id === templateId);
    if (this.template) {
      this.templateDescriptionItems =
        this.currentUser.language === UserLanguage.Swedish
          ? this.template.descriptionItems?.itemsSwedish
          : this.template.descriptionItems?.items;
      this.templateName = CampaignTemplatesService.getTemplateName(this.template, this.currentUser.language);
      this.authorName = this.template.author?.name || 'Team Triggerbee';
      this.authorImage = this.template.author?.image || '/assets/img/bullet-black-logo.svg';
      this.authorUrl = this.template.author?.url || 'https://triggerbee.com';
      this.showTemplatesNavigation = this.templates.length > 1;
      this.isFirstTemplate = this.currentTemplateIndex === 0;
      this.isLastTemplate = this.currentTemplateIndex + 1 === this.templates.length;
    } else {
      this.logger.warn(`No template found by ID: ${templateId}`);
    }
  }

  // The non-mobile view is a tablet (768×1024, the most common tablet resolution) for native-app campaigns.
  public get isTabletView(): boolean {
    return this.isNativeApp && this.currentDeviceView === WidgetDeviceView.Desktop;
  }

  private setIframeSettings() {
    // Device-sized viewport, scaled to fit the frame
    if (this.currentDeviceView === WidgetDeviceView.Mobile) {
      // Same screen as the editor's phone (widget-editor-canvas .smartphone-canvas)
      this.iframeScale = 1;
      this.iframeWidth = 360;
      this.iframeHeight = 640;
    } else if (this.isTabletView) {
      this.iframeScale = 570 / 768;
      this.iframeWidth = 768;
      this.iframeHeight = 1024;
    } else {
      this.iframeScale = 0.75;
      this.iframeWidth = WidgetService.flyoutPreviewWidth / this.iframeScale;
      this.iframeHeight = 600 / this.iframeScale;
    }

    // Set URL (even if already set) to refresh whole iframe and for example re-run animations
    const shouldUseOurPreviewSite = this.isTemplatePreview || !this.currentAccount.hasVisits;
    // previewSiteId is set when the campaign lives in another account, so the preview renders
    // against its own site rather than whichever one is active.
    const previewSiteId = this.flyoutOpeningData.widgetPreviewContext?.previewSiteId ?? this.currentAccount.siteId;
    const params = WidgetService.getPreviewParameters(
      this.currentWidgetId,
      previewSiteId,
      this.isTemplatePreview,
      shouldUseOurPreviewSite,
    );
    // tb-quick-preview lets a product tour anchor to a placeholder, since the preview site lacks its target
    this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      `${this.environment.previewSiteUrl}${params}&tb-quick-preview=true`,
    );
  }

  private rerouteToWidgetEditor(campaign: OnsiteCampaign) {
    if (campaign.type === OnsiteCampaignType.ABTesting) {
      this.widgetService.getWidgetsByCampaignId(campaign.id).subscribe((widgets: Array<Widget>) => {
        this.router.navigate([`/campaigns/ab-tests/${campaign.id}/widgets/${widgets[0].id}/editor/`]);
      });
      return;
    }
    this.router.navigate([`/campaigns/${campaign.id}/widgets/${campaign.widgets[0].id}/editor/`], {
      queryParams: { isNewWidget: true },
    });
  }

  private getCurrentTemplate(): CampaignTemplate {
    return this.templates[this.currentTemplateIndex];
  }

  private switchTemplate() {
    this.isLoading = true;
    const template = this.getCurrentTemplate();
    const widgetId = template.widgets[0].id;
    this.widgetService.prepareWidgetPreview(widgetId).subscribe(
      () => {
        this.campaignId = template.id;
        this.currentWidgetId = widgetId;
        this.setIframeSettings();
        this.setTemplateSettings(template.id);
        this.isLoading = false;
      },
      () => {
        this.isLoading = false;
      },
    );
  }

  public previousTemplate() {
    this.currentTemplateIndex--;
    this.switchTemplate();
  }

  public nextTemplate() {
    this.currentTemplateIndex++;
    this.switchTemplate();
  }

  public changeCurrentDeviceView(deviceView: WidgetDeviceView): void {
    this.currentDeviceView = deviceView;
    this.setIframeSettings();
  }

  // Same as picking it on the templates page: a referral half is only created together with its other half.
  public pickTemplate() {
    const { targetDevice, isAbTest } = this.flyoutOpeningData.widgetPreviewContext;
    const referralType =
      !isAbTest &&
      targetDevice !== TargetDevice.NativeApp &&
      this.referralsAllowed() &&
      this.template &&
      CampaignTemplatesService.getReferralType(this.template);
    if (referralType) {
      this.flyoutService.open({
        type: FlyoutDataType.Referral,
        subType: ReferralTypes.Referring,
        currentAccount: this.currentAccount,
        preselectedTemplate: { id: this.template.id, type: referralType },
      });
      return;
    }
    const dialog = this.campaignService.showCampaignProgressDialog();
    this.campaignService
      .createOnsiteCampaignFromTemplate(
        this.campaignId,
        isAbTest ? OnsiteCampaignType.ABTesting : OnsiteCampaignType.Regular,
        this.currentAccount.defaultFontFamily,
        OnsiteCampaignType.Regular,
        this.purposeId,
        this.currentAccount.isPoweredByDisabled,
        this.currentAccount.isAttributionActivated,
        targetDevice,
      )
      .subscribe(
        (campaign: OnsiteCampaign) => {
          this.logger.debug('Campaign after creation: ' + JSON.stringify(campaign));
          this.rerouteToWidgetEditor(campaign);
          this.campaignService.closeCampaignProgressDialog(dialog);
          this.campaignService.addTagToCampaignFromNewTemplatesCategories(
            Object.values(TemplateTypeForNewCategories),
            this.template,
            campaign,
          );
        },
        (error) => {
          this.logger.error('Error that should break the page: ' + JSON.stringify(error));
          this.campaignService.closeCampaignProgressDialog(dialog);
        },
      );
  }
}
